import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';
import { Magnetic } from './Motion';
import { useTheme } from './ThemeProvider';

const NAV_ITEMS = [
  { id: 'index', label: 'Home', path: '/' },
  { id: 'studio', label: 'About', path: '/about' },
  { id: 'work', label: 'Work', path: '/work' },
  { id: 'ailab', label: 'AI Lab', path: '/ai' },
  { id: 'journal', label: 'Journal', path: '/blog' },
];

const WORK_PATHS = ['/work', '/ux-folio', '/website-folio', '/illustration', '/ai-art'];
const MOBILE_ANIM = { initial: { height: 0 }, animate: { height: 'auto' }, exit: { height: 0 } };

const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="relative w-10 h-10 rounded-full inline-flex items-center justify-center transition-colors"
      style={{ border: '1px solid var(--line-strong)', color: 'var(--fg)' }}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={theme}
          initial={{ rotate: -90, opacity: 0 }}
          animate={{ rotate: 0, opacity: 1 }}
          exit={{ rotate: 90, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="inline-flex"
        >
          {isDark ? <Sun size={15}/> : <Moon size={15}/>}
        </motion.span>
      </AnimatePresence>
    </button>
  );
};

const NavLinks = ({ pathname }) => (
  <nav className="hidden md:flex items-center gap-1 glass rounded-full px-2 py-1.5">
    {NAV_ITEMS.map((it) => {
      const active = pathname === it.path || (it.id === 'work' && (WORK_PATHS.includes(pathname) || pathname.startsWith('/work/') || pathname.startsWith('/website/')));
      return (
        <Link
          key={it.id}
          to={it.path}
          className={`px-4 py-2 rounded-full text-[13px] transition-colors ${active ? 'bg-[color:var(--accent)] text-[color:var(--bg)]' : 'hover:bg-white/5'}`}
        >
          {it.label}
        </Link>
      );
    })}
  </nav>
);

const MobileMenu = ({ onClose }) => (
  <motion.div
    {...MOBILE_ANIM}
    className="md:hidden overflow-hidden"
    style={{ background: 'rgba(11,11,16,0.96)' }}
  >
    <div className="flex flex-col gap-3 px-6 py-6">
      {NAV_ITEMS.map((it) => (
        <Link key={it.id} to={it.path} onClick={onClose} className="text-xl font-display">
          {it.label}
        </Link>
      ))}
      <a href="#contact" onClick={onClose} className="btn-pill solid mt-2 w-fit">
        Let&apos;s talk &rarr;
      </a>
    </div>
  </motion.div>
);

export default function Navbar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const onScroll = useCallback(() => {
    setScrolled(window.scrollY > 30);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const closeMenu = useCallback(() => setOpen(false), []);
  const toggleMenu = useCallback(() => setOpen((v) => !v), []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled ? 'rgba(11,11,16,0.7)' : 'transparent',
        backdropFilter: scrolled ? 'blur(18px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--line)' : '1px solid transparent',
      }}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
  <img
    src="/logo.png"
    alt="AR Folio"
    className="h-16 md:h-[68px] w-auto object-contain my-[-12px]"
  />
</Link>

        <NavLinks pathname={location.pathname} />

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <Magnetic>
            <a href="#contact" className="btn-pill solid">Let&apos;s talk &rarr;</a>
          </Magnetic>
        </div>

        <button className="md:hidden flex flex-col gap-1.5" onClick={toggleMenu} aria-label="Menu">
          <span className="w-7 h-px bg-white"></span>
          <span className="w-5 h-px bg-white ml-auto"></span>
        </button>
      </div>

      <AnimatePresence>{open && <MobileMenu onClose={closeMenu} />}</AnimatePresence>
    </header>
  );
}